// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Intelligence API Client (Advisor UI)
 *
 * Mirrors the client-ui's intelligenceApi but adds the FUND_RESEARCH
 * use case used by the Fund Center chatbot. Calls land on
 * /api/intelligence/** which the Advisor BFF proxies (with SSE-aware
 * piping for the messages endpoint) into intelligence-service.
 *
 * Authentication is via the OAuth2 cookie the BFF stamps on every
 * request — the same one fundsApi etc. depend on.
 */

import api from './advisorApi';

/**
 * Mirror of {@code advisorApi.ts}'s hostname → subdomain extraction.
 * Used by raw {@code fetch()} callers (SSE streaming, file downloads) that
 * bypass the axios interceptor and would otherwise leave the BFF without an
 * {@code X-Brokerage-Id} header — which downstream intelligence-service
 * rejects with INTELLIGENCE_TENANT_REQUIRED.
 */
function brokerageIdFromHostname(): string {
  const hostname = globalThis.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'tb1';
  const parts = hostname.split('.');
  return parts[0] || 'tb1';
}

export type IntelligenceUseCaseCode =
    | 'ADVISOR_RESEARCH'
    | 'FUND_RESEARCH'
    | 'ADVISOR_COMPANION'
    | 'SOS_BUILDER'
    | 'CASHFLOW_COPILOT'
    | 'REPORT_BUILDER';

/**
 * Per-brokerage voice configuration. Resolved from
 * `chat_use_cases.voice_profile` merged with `chat_settings.voice_profile_override`
 * on the IS side and shipped down in the session-create response.
 */
export interface VoiceProfile {
  enabled?: boolean;
  locale?: string;
  gender?: 'female' | 'male' | 'any';
  rate?: number;
  pitch?: number;
  preferredVoices?: string[];
  greeting?: string;
}

export interface CreateSessionResponse {
  sessionId: string;
  useCaseCode: IntelligenceUseCaseCode;
  allowedTools: string[];
  llmProvider: string;
  model: string;
  expiresAt: string;
  agentName?: string;
  /** True when the brokerage admin has opted into voice for FUND_RESEARCH. */
  voiceEnabled?: boolean;
  voiceProfile?: VoiceProfile;
}

/**
 * Mirror of intelligence-service's {@code Proposal} sealed type for the
 * Advisor Companion's propose_* write surface (Phase 4 of the
 * Advisor Companion PRD).
 */
export interface Proposal {
  id: string;
  sessionId: string;
  toolName: string;
  type: 'ADD_NOTE' | 'CREATE_RELATIONSHIP' | 'CREATE_CLIENT' | 'ADD_POLICY';
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED';
  previewMarkdown?: string;
  executedActionId?: string;
}

export interface ProposalResponse extends Proposal {
  /** Type-specific payload (e.g. AddNoteProposal fields) echoed back by the server. */
  payload?: Record<string, unknown>;
}

export interface ConfirmProposalResponse {
  proposalId: string;
  status: Proposal['status'];
  executedActionId?: string;
}

export interface ChatSessionMetadata {
  id: string;
  status: 'ACTIVE' | 'COMPLETED' | 'VOIDED' | 'EXPIRED' | 'FAILED';
  startedAt: string;
  lastActivityAt: string;
  completedAt?: string | null;
  expiresAt: string;
  llmProvider: string;
  model: string;
  messageCount: number;
  toolCallCount: number;
}

export const intelligenceApi = {
  /**
   * Create a new chat session for a use case. `profileId` anchors the
   * session to one relationship — required for CASHFLOW_COPILOT; the
   * SOS_BUILDER use case passes the proposal's profile.
   */
  createSession: async (
    useCaseCode: IntelligenceUseCaseCode,
    profileId?: string,
  ): Promise<CreateSessionResponse> => {
    const { data } = await api.post<CreateSessionResponse>('/intelligence/sessions', {
      useCaseCode,
      ...(profileId ? { profileId } : {}),
    });
    return data;
  },

  /** Fetch the metadata for an existing session (status + counts). */
  getSession: async (sessionId: string): Promise<ChatSessionMetadata> => {
    const { data } = await api.get<ChatSessionMetadata>(
      `/intelligence/sessions/${sessionId}`,
    );
    return data;
  },

  /** Void an in-flight session (user navigated away or clicked End Chat). */
  voidSession: async (sessionId: string): Promise<ChatSessionMetadata> => {
    const { data } = await api.delete<ChatSessionMetadata>(
      `/intelligence/sessions/${sessionId}`,
    );
    return data;
  },

  /**
   * Download a session export (PDF or CSV) as a browser file save.
   * Goes through axios so the {@code X-Brokerage-Id} interceptor stamps
   * the tenant header; {@code window.open} can't set headers and would
   * leave us at the mercy of the BFF's hostname fallback.
   */
  /**
   * Fetch a pending propose-* proposal. The ConfirmationCard hits this
   * on mount so the card always renders the server-side preview (not
   * a copy the LLM may have mis-quoted in chat).
   */
  getProposal: async (sessionId: string, proposalId: string): Promise<ProposalResponse> => {
    const { data } = await api.get<ProposalResponse>(
      `/intelligence/sessions/${sessionId}/proposals/${proposalId}`,
    );
    return data;
  },

  /** Dispatch the write the proposal describes. Single-use; second call returns 410. */
  confirmProposal: async (
    sessionId: string,
    proposalId: string,
    body?: { editedAddNote?: unknown },
  ): Promise<ConfirmProposalResponse> => {
    const { data } = await api.post<ConfirmProposalResponse>(
      `/intelligence/sessions/${sessionId}/proposals/${proposalId}/confirm`,
      body ?? {},
    );
    return data;
  },

  /** Mark cancelled — no write fires. Idempotent for already-consumed proposals. */
  cancelProposal: async (
    sessionId: string,
    proposalId: string,
  ): Promise<ConfirmProposalResponse> => {
    const { data } = await api.post<ConfirmProposalResponse>(
      `/intelligence/sessions/${sessionId}/proposals/${proposalId}/cancel`,
      {},
    );
    return data;
  },

  exportSession: async (sessionId: string, format: 'pdf' | 'csv'): Promise<void> => {
    const response = await api.get<Blob>(
      `/intelligence/sessions/${sessionId}/export.${format}`,
      { responseType: 'blob' },
    );
    const blob = response.data;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${sessionId}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ── SSE streaming ──────────────────────────────────────────────────────

/**
 * Inline attachment shape that mirrors intelligence-service's
 * {@code ChatAttachment} DTO. Browser reads the file, base64-encodes
 * its bytes, and ships it alongside the message content.
 *
 * Caps + media-type allowlist are enforced server-side; the UI
 * mirrors the limits so users get a fast in-browser reject rather
 * than a round-trip 413.
 */
export interface ChatAttachment {
  /** Original filename (used by the LLM as context only). */
  name: string;
  /** IANA media type — application/pdf or image/(png|jpeg|webp|gif). */
  mediaType: string;
  /** Base64-encoded file bytes (no data: URL prefix). */
  data: string;
}

/**
 * SSE handler signature for chat-message streaming. Matches the events
 * intelligence-service emits: `token`, `tool_call_started`,
 * `tool_call_completed`, `done`, `error`.
 *
 * `tool_call_started.name` carries the tool's identifier (e.g.
 * `search_funds`) — the chatbot uses it to render the
 * "🔍 Searching the catalogue…" indicator while the tool is running.
 */
/** A clickable source surfaced by the reference (Tax Agent) tools. */
export interface Citation {
  label: string;
  url?: string;
  edition?: string;
}

export interface ChatStreamHandlers {
  onToken?: (delta: string) => void;
  onToolCallStarted?: (call: { id: string; name: string; input: unknown }) => void;
  onToolCallCompleted?: (result: {
    id: string;
    name?: string;
    durationMs?: number;
    result_size_bytes?: number;
    /** True when the tool returned an error result (guard rejections, RS 4xx…). */
    isError?: boolean;
    /** Clickable sources — emitted for the reference (Tax Agent) tools only. */
    citations?: Citation[];
  }) => void;
  onDone?: (final: { stopReason: string; usage?: unknown }) => void;
  onError?: (msg: string) => void;
}

/**
 * Send a user message and stream the assistant's reply via the Fetch
 * streaming API. EventSource only supports GET, so we POST + parse SSE
 * by hand.
 *
 * Returns an abort function — call it from a useEffect cleanup so
 * unmount tears down the request cleanly.
 *
 * The optional `pageContext` argument carries the page Fin is
 * answering from — entity ids, page type, url — so the LLM can resolve
 * "this", "here", and other deictics without the advisor naming the
 * entity. Server-side validates the shape and prepends a
 * `[CURRENT PAGE]` block to the user message; UNKNOWN page contexts
 * are omitted entirely.
 */
export function streamMessage(
  sessionId: string,
  content: string,
  handlers: ChatStreamHandlers,
  attachments?: ChatAttachment[],
  pageContext?: import('../types/pageContext').AdvisorPageContext,
): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const response = await fetch(`/api/intelligence/sessions/${sessionId}/messages`, {
        method: 'POST',
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          // Raw fetch() bypasses the axios interceptor in advisorApi.ts,
          // so we stamp the tenant header by hand. Advisor BFF also has a
          // hostname-derived fallback for defence in depth, but injecting
          // here keeps the request shape identical to every other call.
          'X-Brokerage-Id': brokerageIdFromHostname(),
        },
        body: JSON.stringify({
          content,
          ...(attachments && attachments.length > 0 ? { attachments } : {}),
          ...(pageContext && pageContext.pageType !== 'UNKNOWN'
            ? { pageContext }
            : {}),
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        handlers.onError?.(`HTTP ${response.status}: ${text || response.statusText}`);
        return;
      }
      if (!response.body) {
        handlers.onError?.('Empty SSE response');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // SSE frames are separated by blank lines; each event has
      // `event:` and `data:` lines. Parse incrementally as the bytes arrive.

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let frameEnd: number;
        while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, frameEnd);
          buffer = buffer.slice(frameEnd + 2);
          dispatchFrame(frame, handlers);
        }
      }
      if (buffer.trim().length > 0) dispatchFrame(buffer, handlers);
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      handlers.onError?.(err instanceof Error ? err.message : 'Stream failed');
    }
  })();

  return () => controller.abort();
}

/**
 * Send a message and resolve with the fully-accumulated assistant reply text
 * once the turn completes. Built on top of {@link streamMessage} — it just
 * discards the incremental streaming and awaits the final text, which is all
 * the Statement of Suitability builder needs (it applies the finished HTML
 * to a section rather than rendering a live chat transcript).
 */
export function sendMessageAwaitingReply(
  sessionId: string,
  content: string,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    let text = '';
    streamMessage(sessionId, content, {
      onToken: (delta) => {
        text += delta;
      },
      onDone: () => resolve(text),
      onError: (msg) => reject(new Error(msg)),
    });
  });
}

function dispatchFrame(frame: string, handlers: ChatStreamHandlers) {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).trim());
  }
  if (dataLines.length === 0) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(dataLines.join('\n'));
  } catch {
    parsed = dataLines.join('\n');
  }
  switch (event) {
    case 'token':
      handlers.onToken?.((parsed as { delta?: string })?.delta ?? '');
      break;
    case 'tool_call_started':
      handlers.onToolCallStarted?.(parsed as { id: string; name: string; input: unknown });
      break;
    case 'tool_call_completed':
      handlers.onToolCallCompleted?.(
        parsed as { id: string; name?: string; durationMs?: number; isError?: boolean; citations?: Citation[] },
      );
      break;
    case 'done':
      handlers.onDone?.(parsed as { stopReason: string; usage?: unknown });
      break;
    case 'error':
      handlers.onError?.((parsed as { message?: string })?.message ?? 'error');
      break;
    default:
      // Ignore unknown events for forward-compat.
      break;
  }
}
